import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Divider,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
  Paper,
  useTheme,
  alpha,
  TextField,
  InputAdornment,
  IconButton,
  Badge,
  Stack
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  Chat as ChatIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  ArrowForward as ArrowForwardIcon,
  Videocam as VideocamIcon,
  Call as CallIcon,
  Message as MessageIcon
} from '@mui/icons-material';
import ConsultationDetail from '../components/ConsultationDetail/ConsultationDetail';
import { mockConsultations } from '../mockData/consultations';
import { fetchIntercomConversations } from '../services/intercomService';

function PrivateHealthConsultantHub() {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    // In a real app, this would fetch from the Intercom API
    // For now, we'll use mock data
    fetchConsultations();
  }, []);

  const fetchConsultations = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // In a real implementation, this would call the Intercom API
      // const conversations = await fetchIntercomConversations();
      
      // For now, we'll use mock data
      setTimeout(() => {
        setConsultations(mockConsultations);
        setLoading(false);
      }, 1000);
    } catch (err) {
      console.error('Error fetching consultations:', err);
      setError('Failed to load consultations. Please try again later.');
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setSelectedConsultation(null);
  };

  const handleConsultationSelect = (consultation) => {
    setSelectedConsultation(consultation);
  };

  const handleBackToList = () => {
    setSelectedConsultation(null);
    // Refresh the list to get the latest status
    fetchConsultations();
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const handleSearch = () => {
    if (!searchQuery.trim()) return;
    
    setIsSearching(true);
    
    // In a real implementation, this would search the Intercom API
    // For now, we'll filter the mock data
    const filteredConsultations = mockConsultations.filter(c => 
      c.patientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.complaints.toLowerCase().includes(searchQuery.toLowerCase())
    );
    
    setConsultations(filteredConsultations);
    setIsSearching(false);
  };

  const getFilteredConsultations = () => {
    if (tabValue === 0) {
      return consultations.filter(c => c.status === 'new');
    } else if (tabValue === 1) {
      return consultations.filter(c => c.status === 'active');
    } else {
      return consultations.filter(c => c.status === 'resolved');
    }
  };

  const getStatusChip = (status) => {
    let color;
    let label;

    switch (status) {
      case 'new':
        color = 'primary';
        label = 'New';
        break;
      case 'active':
        color = 'success';
        label = 'Active';
        break;
      case 'resolved':
        color = 'default';
        label = 'Resolved';
        break;
      default:
        color = 'default';
        label = status;
    }

    return (
      <Chip 
        label={label} 
        color={color} 
        size="small" 
        sx={{ fontWeight: 'bold' }}
      />
    );
  };

  // Get communication preference badge
  const getCommunicationBadge = (preference) => {
    let icon;
    let color;
    let label;

    switch (preference) {
      case 'video':
        icon = <VideocamIcon fontSize="small" />;
        color = 'error';
        label = 'Video Call';
        break;
      case 'call':
        icon = <CallIcon fontSize="small" />;
        color = 'success';
        label = 'Voice Call';
        break;
      case 'chat':
      default:
        icon = <MessageIcon fontSize="small" />;
        color = 'info';
        label = 'Messaging';
    }

    return (
      <Chip
        icon={icon}
        label={label}
        color={color}
        size="small"
        variant="outlined"
        sx={{ 
          borderRadius: '16px',
          '& .MuiChip-icon': { 
            color: `${theme.palette[color].main} !important`,
            ml: 0.5
          }
        }}
      />
    );
  };

  if (loading && consultations.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && consultations.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const filteredConsultations = getFilteredConsultations();

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, mt: 0 }}>
            Private Health Consultant Hub
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Manage patient consultations and provide real-time health advice
          </Typography>
        </Box>
        <Button 
          variant="outlined" 
          onClick={fetchConsultations}
          startIcon={<RefreshIcon />}
          sx={{ 
            borderRadius: 8,
            px: 2,
            py: 1,
            borderColor: alpha(theme.palette.primary.main, 0.5),
            '&:hover': {
              borderColor: theme.palette.primary.main,
              bgcolor: alpha(theme.palette.primary.main, 0.04)
            }
          }}
        >
          Refresh
        </Button>
      </Box>

      {selectedConsultation ? (
        <Box>
          <Box sx={{ mb: 2 }}>
            <Typography 
              variant="body1" 
              sx={{ 
                cursor: 'pointer', 
                color: 'primary.main',
                display: 'inline-flex',
                alignItems: 'center',
                '&:hover': { textDecoration: 'underline' }
              }}
              onClick={handleBackToList}
            >
              ← Back to list
            </Typography>
          </Box>
          <ConsultationDetail 
            consultation={selectedConsultation} 
            onClose={handleBackToList}
          />
        </Box>
      ) : (
        <>
          <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <TextField
              fullWidth
              placeholder="Search by patient name or complaint"
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="clear search"
                      onClick={handleClearSearch}
                      edge="end"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ flexGrow: 1 }}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Button 
              variant="contained" 
              onClick={handleSearch}
              disabled={!searchQuery.trim() || isSearching}
              sx={{ 
                minWidth: '120px',
                borderRadius: 8,
                px: 3
              }}
            >
              {isSearching ? 'Searching...' : 'Search'}
            </Button>
          </Box>

          <Paper sx={{ mb: 4 }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
              sx={{ 
                borderBottom: `1px solid ${theme.palette.divider}`,
                '& .MuiTab-root': {
                  py: 2,
                  fontWeight: 600
                }
              }}
            >
              <Tab label={`New (${consultations.filter(c => c.status === 'new').length})`} />
              <Tab label={`Active (${consultations.filter(c => c.status === 'active').length})`} />
              <Tab label={`Resolved (${consultations.filter(c => c.status === 'resolved').length})`} />
            </Tabs>

            {filteredConsultations.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No consultations found.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  {filteredConsultations.map((consultation) => (
                    <Grid item xs={12} sm={6} md={4} key={consultation.id}>
                      <Card
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          height: '100%',
                          cursor: 'pointer',
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: 4
                          }
                        }}
                        onClick={() => handleConsultationSelect(consultation)}
                      >
                        <CardContent sx={{ p: 0, flexGrow: 1 }}>
                          <Box sx={{ p: 2, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 1.5 }}>
                                <PersonIcon />
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600 }}>
                                  {consultation.patientName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {consultation.patientAge} years, {consultation.patientGender}
                                </Typography>
                              </Box>
                            </Box>
                            {getStatusChip(consultation.status)}
                          </Box>

                          <Divider />

                          <Box sx={{ p: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <AccessTimeIcon fontSize="small" sx={{ mr: 0.5, fontSize: '0.9rem' }} />
                              {new Date(consultation.timestamp).toLocaleString()}
                            </Typography>
                            
                            <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                              {consultation.complaints}
                            </Typography>
                            
                            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                              {consultation.communicationPreference && 
                                getCommunicationBadge(consultation.communicationPreference)
                              }
                            </Stack>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <ChatIcon fontSize="small" sx={{ mr: 0.5, color: theme.palette.text.secondary }} />
                                <Typography variant="body2" color="text.secondary">
                                  {consultation.messageCount} messages
                                </Typography>
                              </Box>
                              <Button
                                endIcon={<ArrowForwardIcon />}
                                size="small"
                                sx={{ fontWeight: 600 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleConsultationSelect(consultation);
                                }}
                              >
                                View
                              </Button>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
}

export default PrivateHealthConsultantHub;
